/**
 * Who Am I? reducer (continuous-play letter-reveal). Pure function: state +
 * action -> next state (or same reference when nothing changed).
 *
 * Scene routing rules:
 *   - LOBBY snapshot                              -> lobby
 *   - wai_round_open                              -> round
 *   - wai_letter_reveal / wai_correct_answer      -> round (accumulates)
 *   - wai_round_end                               -> reveal
 *   - wai_game_over                               -> podium
 *   - WS connection lost                          -> connection_lost
 *
 * `wai_session_leaderboard` updates state.sessionLeaderboard but does not
 * change the scene; the side panel reads it directly to render the
 * "THIS SESSION" view of the auto-toggling leaderboard.
 */

import type { OutMsg } from '@sg/shared';
import type { BrowserState, SceneName } from './types.js';

export type Action =
  | { type: 'ws'; msg: OutMsg }
  | { type: 'connection'; value: BrowserState['connection'] };

export function initialState(): BrowserState {
  return {
    scene: 'lobby',
    connection: 'idle',
    tenantId: '',
    state: 'LOBBY',
    theme: null,
    leaderboard: [],
    leaderboardAllTime: [],
    sessionLeaderboard: [],
    round: null,
    reveal: null,
    podium: null,
  };
}

export function reduce(state: BrowserState, action: Action): BrowserState {
  if (action.type === 'connection') {
    if (state.connection === action.value) return state;
    const scene: SceneName = action.value === 'open' ? state.scene : 'connection_lost';
    return { ...state, connection: action.value, scene };
  }
  return reduceWs(state, action.msg);
}

function reduceWs(state: BrowserState, msg: OutMsg): BrowserState {
  switch (msg.type) {
    case 'state_snapshot': {
      const s = msg.state;
      const isLobby = s.state.startsWith('LOBBY');
      const isPodium = s.state.startsWith('PODIUM');
      return {
        ...state,
        tenantId: s.tenant_id,
        state: s.state,
        theme: s.theme ?? state.theme,
        leaderboard: s.leaderboard_top10 ?? state.leaderboard,
        leaderboardAllTime: s.leaderboard_all_time ?? state.leaderboardAllTime,
        // Reset session standings on lobby (new game) so stale data doesn't
        // bleed across game sessions.
        sessionLeaderboard: isLobby ? [] : state.sessionLeaderboard,
        round: isLobby ? null : state.round,
        reveal: isLobby ? null : state.reveal,
        podium: isLobby ? null : state.podium,
        scene: isLobby ? 'lobby' : isPodium ? 'podium' : state.scene,
      };
    }
    case 'wai_round_open': {
      return {
        ...state,
        scene: 'round',
        reveal: null,
        round: {
          subjectId: msg.subject_id,
          category: msg.category,
          index: msg.round_index,
          total: msg.rounds_total,
          totalLetters: msg.total_letters,
          letterRevealIntervalMs: msg.letter_reveal_interval_ms,
          startedAtMs: msg.started_at_ms,
          endsAtMs: msg.ends_at_ms,
          roundSeconds: msg.round_seconds,
          rendered: msg.initial_render,
          revealedLetters: [],
          revealedCount: 0,
          nextRevealAtMs: msg.started_at_ms + msg.letter_reveal_interval_ms,
          corrects: [],
        },
      };
    }
    case 'wai_letter_reveal': {
      const r = state.round;
      if (!r || r.subjectId !== msg.subject_id) {
        // Out-of-band reveal — synthesize a minimal round so the reveal still
        // renders rather than dropping silently.
        return {
          ...state,
          scene: 'round',
          round: {
            subjectId: msg.subject_id,
            category: '',
            index: 0,
            total: 1,
            totalLetters: msg.total_letters,
            letterRevealIntervalMs: 5_000,
            startedAtMs: msg.revealed_at_ms,
            endsAtMs: msg.revealed_at_ms + 60_000,
            roundSeconds: 60,
            rendered: msg.revealed_letters,
            revealedLetters: [msg.letter],
            revealedCount: msg.revealed_count,
            nextRevealAtMs: msg.next_reveal_at_ms,
            corrects: [],
          },
        };
      }
      if (r.revealedLetters.includes(msg.letter)) {
        return { ...state, round: { ...r, nextRevealAtMs: msg.next_reveal_at_ms } };
      }
      return {
        ...state,
        scene: 'round',
        round: {
          ...r,
          rendered: msg.revealed_letters,
          revealedLetters: [...r.revealedLetters, msg.letter],
          revealedCount: msg.revealed_count,
          nextRevealAtMs: msg.next_reveal_at_ms,
        },
      };
    }
    case 'wai_correct_answer': {
      const r = state.round;
      if (!r) return state;
      // Append in arrival order; ranks come from the backend so they're stable.
      return {
        ...state,
        round: {
          ...r,
          corrects: [
            ...r.corrects,
            {
              rank: msg.rank,
              player_id: msg.player_id,
              display_name: msg.display_name,
              points: msg.points,
              at_ms: msg.at_ms,
              revealed_count: msg.revealed_count,
            },
          ],
        },
      };
    }
    case 'wai_round_end': {
      return {
        ...state,
        scene: 'reveal',
        reveal: {
          subjectId: msg.subject_id,
          subjectName: msg.subject_name,
          category: msg.category,
          imageUrl: msg.image_url,
          index: msg.round_index,
          total: msg.rounds_total,
          totalLetters: msg.total_letters,
          revealedCount: msg.revealed_count,
          corrects: msg.correct_answers.map((c) => ({
            rank: c.rank,
            player_id: c.player_id,
            display_name: c.display_name,
            points: c.points,
            at_ms: c.at_ms,
            revealed_count: c.revealed_count,
          })),
          reason: msg.reason,
        },
      };
    }
    case 'wai_session_leaderboard': {
      return {
        ...state,
        sessionLeaderboard: msg.rows.map((r) => ({
          rank: r.rank,
          player_id: r.player_id,
          display_name: r.display_name,
          total_points: r.total_points,
        })),
      };
    }
    case 'wai_game_over': {
      return {
        ...state,
        scene: 'podium',
        podium: msg.podium,
      };
    }
    default:
      return state;
  }
}
